import type { ProductContainerDesign } from './productContainerTypes';
import type { LabelFieldKey, LabelLayoutTemplate } from './labelLayoutEngine';
import { DEFAULT_LABEL_FIELD_ORDER, layoutStyleToTemplate } from './labelLayoutEngine';
import { DEFAULT_CONTAINER_DESIGN, mergeContainerDesign } from './productContainerTypes';
import type { LabelCanvasElement } from './labelCanvasTypes';
import { mergeCanvasElements } from './labelCanvasTypes';

export type LabelCanvasWrapGuide = 'none' | '2-panel' | '3-panel';

export type ProductLabelPreset =
  | 'SHELF_2X1'
  | 'SHELF_2_25X1_25'
  | 'SHELF_4X2'
  | 'SHELF_3X2'
  | 'SHIPPING_4X6'
  | 'CUSTOM';

/** @deprecated use layoutTemplate */
export type ProductLabelLayoutStyle =
  | 'wrap'
  | 'classic'
  | 'sticker'
  | 'vertical'
  | 'nutrition';

export type LabelExtraImage = {
  id: string;
  url: string;
  label?: string;
};

export type ProductLabelLayout = {
  layoutTemplate: LabelLayoutTemplate;
  /** @deprecated migrated to layoutTemplate */
  layoutStyle?: ProductLabelLayoutStyle;
  autoFit: boolean;
  showImage: boolean;
  showName: boolean;
  showSku: boolean;
  showBarcode: boolean;
  showHsCode: boolean;
  showCountryOfOrigin: boolean;
  showUnit: boolean;
  showPrice: boolean;
  showPackaging: boolean;
  showBrand: boolean;
  showDescription: boolean;
  showBenefits: boolean;
  showCategory: boolean;
  showNetQuantity: boolean;
  showCompanyInfo: boolean;
  showMarketing: boolean;
  showExpiry: boolean;
  brandName: string;
  footerText: string;
  marketingTagline: string;
  marketingHashtag: string;
  companyPhone: string;
  companyWebsite: string;
  companyLegalLine: string;
  backgroundColor: string;
  textColor: string;
  accentColor: string;
  secondaryBackgroundColor: string;
  panelBorderColor: string;
  legalPanelTextColor: string;
  fontScale: number;
  orientation: 'landscape' | 'portrait';
  containerDesign: ProductContainerDesign;
  extraImages: LabelExtraImage[];
  /** Custom stack order when layoutTemplate is FIELD_ORDER */
  fieldOrder: LabelFieldKey[];
  /** Free-form canvas elements when layoutTemplate is CANVAS */
  canvasElements: LabelCanvasElement[];
  /** Canvas editor: snap elements to grid while dragging */
  canvasSnapEnabled: boolean;
  canvasSnapGridPct: number;
  /** Editor-only fold lines for wrap-around labels (not exported) */
  canvasWrapGuide: LabelCanvasWrapGuide;
};

export type ProductLabelDesign = {
  id: string | null;
  productId: string;
  preset: ProductLabelPreset;
  widthIn: number;
  heightIn: number;
  layout: ProductLabelLayout;
  packageWeightLbs: number | null;
  packageLengthIn: number | null;
  packageWidthIn: number | null;
  packageHeightIn: number | null;
  packageMaterial: string | null;
};

export type ProductWorkspaceDetail = {
  id: string;
  sku: string;
  name: string;
  description?: string | null;
  benefits?: string | null;
  imageUrl?: string | null;
  baseUnit: string;
  unitLabel?: string | null;
  salePrice?: number | string | null;
  pricePerUnit?: number | string | null;
  purchasePricePerUnit?: number | string | null;
  discountPercent?: number | string | null;
  fixedDeliveryFee?: number | string | null;
  typicalExpiryDays?: number | null;
  currentStockLbs: number;
  isActive: boolean;
  hsCode?: string | null;
  countryOfOrigin?: string | null;
  isPreorderEnabled?: boolean;
  preorderEta?: string | null;
  preorderDepositType?: string | null;
  preorderDepositValue?: number | string | null;
  preorderCancellationFeeType?: string | null;
  preorderCancellationFeeValue?: number | string | null;
  preorderMaxQtyLbs?: number | string | null;
  category?: {
    id: string;
    name: string;
    slug?: string;
    department?: { id: string; name: string; slug: string } | null;
  } | null;
  labelDesign?: ProductLabelDesign | null;
  packagingOptions?: Array<{
    id: string;
    label: string;
    sizeLbs: number;
    isDefault?: boolean;
  }>;
};

export const LABEL_PRESET_OPTIONS: Array<{
  value: ProductLabelPreset;
  label: string;
  widthIn: number;
  heightIn: number;
}> = [
  { value: 'SHELF_2X1', label: '2″ × 1″ shelf', widthIn: 2, heightIn: 1 },
  { value: 'SHELF_2_25X1_25', label: '2.25″ × 1.25″', widthIn: 2.25, heightIn: 1.25 },
  { value: 'SHELF_4X2', label: '4″ × 2″ standard', widthIn: 4, heightIn: 2 },
  { value: 'SHELF_3X2', label: '3″ × 2″ medium', widthIn: 3, heightIn: 2 },
  { value: 'SHIPPING_4X6', label: '4″ × 6″ shipping', widthIn: 4, heightIn: 6 },
  { value: 'CUSTOM', label: '8″ × 2″ wrap', widthIn: 8, heightIn: 2 },
  { value: 'CUSTOM', label: '6″ × 2″ wrap', widthIn: 6, heightIn: 2 },
  { value: 'CUSTOM', label: 'Custom size', widthIn: 4, heightIn: 2 },
];

export const DEFAULT_LABEL_LAYOUT: ProductLabelLayout = {
  layoutTemplate: 'HORIZONTAL_SPLIT',
  autoFit: true,
  showImage: true,
  showName: true,
  showSku: true,
  showBarcode: true,
  showHsCode: true,
  showCountryOfOrigin: true,
  showUnit: true,
  showPrice: true,
  showPackaging: true,
  showBrand: true,
  showDescription: true,
  showBenefits: true,
  showCategory: true,
  showNetQuantity: true,
  showCompanyInfo: true,
  showMarketing: false,
  showExpiry: true,
  brandName: 'Lewouhmart',
  footerText: '',
  marketingTagline: '',
  marketingHashtag: '',
  companyPhone: '',
  companyWebsite: 'www.lewouhmart.com',
  companyLegalLine: 'Store refrigerated · Recycle packaging',
  backgroundColor: '#ffffff',
  textColor: '#0f172a',
  accentColor: '#0369a1',
  secondaryBackgroundColor: '#f8fafc',
  panelBorderColor: '#cbd5e1',
  legalPanelTextColor: '#0f172a',
  fontScale: 1,
  orientation: 'landscape',
  containerDesign: DEFAULT_CONTAINER_DESIGN,
  extraImages: [],
  fieldOrder: DEFAULT_LABEL_FIELD_ORDER,
  canvasElements: [],
  canvasSnapEnabled: true,
  canvasSnapGridPct: 5,
  canvasWrapGuide: 'none',
};

export type LabelThemePreset = {
  id: string;
  name: string;
  description: string;
  layout: Partial<ProductLabelLayout>;
};

export const LABEL_THEME_PRESETS: LabelThemePreset[] = [
  {
    id: 'clean-white',
    name: 'Clean professional',
    description: 'White label with navy accent — works on any container',
    layout: {
      layoutTemplate: 'HORIZONTAL_SPLIT',
      backgroundColor: '#ffffff',
      textColor: '#0f172a',
      accentColor: '#0369a1',
      secondaryBackgroundColor: '#f1f5f9',
      panelBorderColor: '#cbd5e1',
      legalPanelTextColor: '#0f172a',
    },
  },
  {
    id: 'dark-premium',
    name: 'Dark premium',
    description: 'Dark background with gold accent',
    layout: {
      layoutTemplate: 'BRAND_LEFT',
      backgroundColor: '#1e293b',
      textColor: '#f8fafc',
      accentColor: '#d4a853',
      secondaryBackgroundColor: '#334155',
      panelBorderColor: '#475569',
      legalPanelTextColor: '#f8fafc',
    },
  },
  {
    id: 'fresh-green',
    name: 'Fresh market',
    description: 'Natural green for produce',
    layout: {
      layoutTemplate: 'GRID_2X2',
      backgroundColor: '#f0fdf4',
      textColor: '#14532d',
      accentColor: '#166534',
      secondaryBackgroundColor: '#ffffff',
      panelBorderColor: '#86efac',
      legalPanelTextColor: '#14532d',
    },
  },
  {
    id: 'compliance',
    name: 'Compliance focus',
    description: 'Regulatory grid — HS, origin, ingredients',
    layout: {
      layoutTemplate: 'COMPLIANCE_GRID',
      backgroundColor: '#ffffff',
      textColor: '#0f172a',
      accentColor: '#0f172a',
      secondaryBackgroundColor: '#f8fafc',
      panelBorderColor: '#0f172a',
      legalPanelTextColor: '#0f172a',
      showMarketing: false,
    },
  },
];

export function normalizeLabelFieldOrder(raw?: LabelFieldKey[] | null): LabelFieldKey[] {
  const seen = new Set<LabelFieldKey>();
  const order: LabelFieldKey[] = [];
  for (const key of raw || []) {
    if (seen.has(key)) continue;
    seen.add(key);
    order.push(key);
  }
  for (const key of DEFAULT_LABEL_FIELD_ORDER) {
    if (!seen.has(key)) order.push(key);
  }
  return order;
}

export function mergeLabelLayout(raw?: Partial<ProductLabelLayout> | null): ProductLabelLayout {
  const merged = { ...DEFAULT_LABEL_LAYOUT, ...(raw || {}) };
  const layoutTemplate =
    merged.layoutTemplate ??
  layoutStyleToTemplate(merged.layoutStyle);
  return {
    ...merged,
    layoutTemplate,
    autoFit: merged.autoFit !== false,
    extraImages: Array.isArray(merged.extraImages) ? merged.extraImages : [],
    fieldOrder: normalizeLabelFieldOrder(merged.fieldOrder),
    canvasElements: mergeCanvasElements(merged.canvasElements),
    canvasSnapEnabled: merged.canvasSnapEnabled !== false,
    canvasSnapGridPct:
      Number.isFinite(merged.canvasSnapGridPct) && merged.canvasSnapGridPct > 0
        ? merged.canvasSnapGridPct
        : 5,
    canvasWrapGuide:
      merged.canvasWrapGuide === '2-panel' || merged.canvasWrapGuide === '3-panel'
        ? merged.canvasWrapGuide
        : 'none',
    containerDesign: mergeContainerDesign(
      (raw?.containerDesign as Partial<ProductContainerDesign> | undefined) ??
        merged.containerDesign,
    ),
  };
}

export function inchesToPx(inches: number, dpi = 96): number {
  return Math.round(inches * dpi);
}

export function truncateText(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1).trim()}…`;
}
